/** QuickHouse — Barre d'outils 2D + panneau de propriétés de la sélection */
import { useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { roomArea, generateWallsFromRooms, pointInRoom } from '../../shared/rooms.js';
import { newId } from '../../shared/schema.js';
import { wallLength, roundCoord, fmtLen } from '../../shared/geometry.js';
import { BEAM_MATERIAL_LABELS } from '../../shared/beams.js';
import { splitWallAt, itemOrigin } from '../editor2d/tools2d.js';
import NumField from './NumField.jsx';
import OutlinePanel from './OutlinePanel.jsx';
import { finishOptions, SHUTTER_COLORS } from '../viewer3d/textures.js';
import { ITEM_CATEGORIES, findCatalogItem } from '../../shared/catalog.js';
import { api } from '../api/client.js';

/** Bibliothèque d'objets : choix de catégorie + objet à poser, import/export JSON */
function CatalogBrowser() {
  const catalog = useProjectStore((s) => s.catalog);
  const placingId = useProjectStore((s) => s.placingId);
  const setPlacingId = useProjectStore((s) => s.setPlacingId);
  const loadCatalog = useProjectStore((s) => s.loadCatalog);
  const setStatus = useProjectStore((s) => s.setStatus);
  const [cat, setCat] = useState('elec');

  const entries = catalog.filter((e) => e.category === cat);

  const importJson = (files) => {
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await api('/catalog', { method: 'POST', body: JSON.parse(reader.result) });
          await loadCatalog();
          setStatus(`Objet « ${file.name} » importé dans la bibliothèque`);
        } catch (e) {
          setStatus(`Import impossible : ${e.message}`);
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="props">
      <select value={cat} onChange={(e) => setCat(e.target.value)}>
        {Object.entries(ITEM_CATEGORIES).map(([id, c]) => (
          <option key={id} value={id}>{c.label}</option>
        ))}
      </select>
      <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {entries.map((e) => (
          <div key={e.id} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <button
              className={placingId === e.id ? 'active' : ''}
              style={{ flex: 1, textAlign: 'left', fontSize: 11, padding: '3px 6px' }}
              onClick={() => setPlacingId(e.id)}
              title={`${e.size.w}×${e.size.d}×${e.size.h} cm — cliquez puis posez sur le plan (R pour tourner)`}
            >
              {e.custom ? '🖼 ' : ''}{e.name}
            </button>
            <a
              href={`/api/catalog/${e.id}`}
              download
              className="mini"
              style={{ fontSize: 10, textDecoration: 'none', color: 'var(--text-dim)' }}
              title="Exporter en JSON (modifiable puis réimportable)"
            >
              ⤓
            </a>
            {e.custom && (
              <button
                className="danger" style={{ padding: '0 5px', fontSize: 10 }} title="Supprimer cet objet personnalisé"
                onClick={async () => { await api(`/catalog/${e.id}`, { method: 'DELETE' }); await loadCatalog(); }}
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      <label style={{ cursor: 'pointer' }}>
        <span style={{ display: 'block', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 4, padding: 5, fontSize: 11 }}>
          📥 Importer un objet (JSON)…
        </span>
        <input type="file" accept="application/json,.json" multiple hidden
          onChange={(e) => { importJson(e.target.files); e.target.value = ''; }} />
      </label>
      <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>
        ⤓ exporte un objet en JSON : modifiez ses dimensions/formes puis réimportez-le
        (changez son « id ») pour créer votre variante.
      </div>
    </div>
  );
}

/** Sélecteur de finition 3D (présets + textures importées) */
function FinishSelect({ label, kind, value, onCommit, autoLabel = 'Aucune (couleur unie)' }) {
  const textures = useProjectStore((s) => s.textures);
  return (
    <label>{label}
      <select value={value ?? ''} onChange={(e) => onCommit(e.target.value || null)}>
        <option value="">{autoLabel}</option>
        {finishOptions(kind, textures).map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

/** Import et gestion des textures du projet */
function TextureManager() {
  const textures = useProjectStore((s) => s.textures);
  const uploadTexture = useProjectStore((s) => s.uploadTexture);
  const removeTexture = useProjectStore((s) => s.removeTexture);
  const setStatus = useProjectStore((s) => s.setStatus);

  const onFiles = (files) => {
    for (const file of files) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) continue;
      const reader = new FileReader();
      reader.onload = () => {
        const data = reader.result.split(',')[1];
        uploadTexture(file.name.replace(/\.\w+$/, ''), file.type, data)
          .then(() => setStatus(`Texture « ${file.name} » importée`))
          .catch((e) => setStatus(`Import impossible : ${e.message}`));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="props">
      <label style={{ cursor: 'pointer' }}>
        <span className="tool-btn" style={{ display: 'block', textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 4, padding: 6 }}>
          🖼 Importer une texture…
        </span>
        <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
          onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} />
      </label>
      {textures.map((t) => (
        <div key={t.id} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <img src={t.url} alt="" style={{ width: 22, height: 22, objectFit: 'cover', borderRadius: 3 }} />
          <span style={{ flex: 1, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
          <button className="danger" style={{ padding: '0 5px', fontSize: 10 }} title="Supprimer"
            onClick={() => removeTexture(t.id)}>✕</button>
        </div>
      ))}
    </div>
  );
}

const TOOLS = [
  { id: 'select', label: 'Sélection', key: 'S' },
  { id: 'wall', label: 'Mur', key: 'W' },
  { id: 'door', label: 'Porte', key: 'P' },
  { id: 'window', label: 'Fenêtre', key: 'F' },
  { id: 'garage', label: 'Garage', key: 'G' },
  { id: 'passage', label: 'Ouverture libre', key: 'O' },
  { id: 'stair', label: 'Escalier', key: 'E' },
  { id: 'room', label: 'Pièce', key: 'T' },
  { id: 'slab', label: 'Dalle', key: 'D' },
  { id: 'roof', label: 'Toit', key: 'C' },
  { id: 'item', label: 'Objet (bibliothèque)', key: 'B' },
  { id: 'delete', label: 'Supprimer', key: 'X' },
];

const SLAB_MATERIAL_LABELS = { concrete: 'Béton', wood: 'Bois', steel: 'Acier', other: 'Autre' };
const ROOF_MATERIAL_LABELS = { slate: 'Ardoise', tile: 'Tuile', flat: 'Plat (gravier)', green: 'Végétalisé' };
const SLOPE_DIR_LABELS = { N: 'Nord (haut du plan)', S: 'Sud (bas du plan)', E: 'Est (droite)', W: 'Ouest (gauche)' };

/** Libellés français des variantes d'ouvertures */
export const VARIANT_LABELS = {
  door: { single: 'Simple battant', double: 'Double battant', doubleGlazed: 'Double battant vitré', sliding: 'Coulissante' },
  window: {
    single: '1 battant', double: '2 battants', bay: 'Baie vitrée', pocket: 'Baie à galandage',
    french: 'Porte-fenêtre 1 vantail', frenchDouble: 'Porte-fenêtre 2 vantaux', velux: 'Velux (toit)',
  },
  garage: { roller: 'À volet roulant', double: 'Double battant', sliding: 'Coulissante' },
};

const STAIR_KINDS = [
  { id: 'straight', label: 'Droit / à angles' },
  { id: 'spiral', label: 'Colimaçon' },
  { id: 'ladder', label: 'Échelle de meunier' },
];

export default function Toolbar() {
  const tool = useProjectStore((s) => s.tool);
  const setTool = useProjectStore((s) => s.setTool);
  const roomMode = useProjectStore((s) => s.roomMode);
  const setRoomMode = useProjectStore((s) => s.setRoomMode);
  const stairKind = useProjectStore((s) => s.stairKind);
  const setStairKind = useProjectStore((s) => s.setStairKind);
  const beamKind = useProjectStore((s) => s.beamKind);
  const setBeamKind = useProjectStore((s) => s.setBeamKind);
  const openingVariants = useProjectStore((s) => s.openingVariants);
  const setOpeningVariant = useProjectStore((s) => s.setOpeningVariant);
  const chatBusy = useProjectStore((s) => s.chatBusy);

  const TOOL_NAMES = {
    select: 'Sélection', wall: 'Mur', door: 'Porte', window: 'Fenêtre', garage: 'Garage',
    passage: 'Ouverture libre', stair: 'Escalier', room: 'Pièce', slab: 'Dalle', roof: 'Toit',
    item: 'Bibliothèque d\'objets', beam: 'Charpente', measure: 'Cote', delete: 'Supprimer',
  };

  return (
    <div className="toolbar">
      <h3>Outil : {TOOL_NAMES[tool] ?? tool}</h3>
      {['door', 'window', 'garage'].includes(tool) && (
        <select
          style={{ width: '100%' }}
          value={openingVariants[tool]}
          onChange={(e) => setOpeningVariant(tool, e.target.value)}
        >
          {Object.entries(VARIANT_LABELS[tool]).map(([v, label]) => (
            <option key={v} value={v}>{label}</option>
          ))}
        </select>
      )}
      {tool === 'stair' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {STAIR_KINDS.map((k) => (
            <button
              key={k.id}
              className={stairKind === k.id ? 'active' : ''}
              style={{ fontSize: 11, padding: '4px 6px', textAlign: 'left' }}
              onClick={() => setStairKind(k.id)}
              disabled={chatBusy}
            >
              {k.label}
            </button>
          ))}
        </div>
      )}
      {tool === 'beam' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <button
            className={beamKind === 'beam' ? 'active' : ''}
            style={{ fontSize: 11, padding: '4px 6px', textAlign: 'left' }}
            onClick={() => setBeamKind('beam')}
            disabled={chatBusy}
            title="Poutre horizontale ou rampante (linteau, panne, chevron, lisse de pergola…) — clic-clic en chaîne"
          >
            ▬ Poutre / linteau
          </button>
          <button
            className={beamKind === 'post' ? 'active' : ''}
            style={{ fontSize: 11, padding: '4px 6px', textAlign: 'left' }}
            onClick={() => setBeamKind('post')}
            disabled={chatBusy}
            title="Poteau vertical (pergola, tonnelle, appui de charpente) — un clic pour poser"
          >
            ▮ Poteau
          </button>
          <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>
            Par défaut : section 15×20 sous plafond (poutre), 15×15 toute hauteur (poteau).
            Élévations et sections dans le panneau propriétés — chevron : élévations a/b différentes.
          </div>
        </div>
      )}
      {tool === 'item' && <CatalogBrowser />}
      {['room', 'slab', 'roof'].includes(tool) && (
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            className={roomMode === 'rect' ? 'active' : ''}
            style={{ flex: 1, fontSize: 11, padding: '4px 6px' }}
            onClick={() => setRoomMode('rect')}
            title="Glissez pour tracer un bloc rectangulaire"
          >
            ▭ Rectangle
          </button>
          <button
            className={roomMode === 'poly' ? 'active' : ''}
            style={{ flex: 1, fontSize: 11, padding: '4px 6px' }}
            onClick={() => setRoomMode('poly')}
            title="Cliquez les sommets, Entrée ou premier point pour fermer"
          >
            ⬠ Polygone
          </button>
        </div>
      )}
      <h3>Pièces → murs</h3>
      <GenerateWallsButton />
      <h3>Textures</h3>
      <TextureManager />
      <h3>Contenu</h3>
      <OutlinePanel />
      <h3>Aide</h3>
      <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6 }}>
        Molette : zoom<br />
        Clic molette / Alt+clic : déplacer la vue<br />
        Glisser sur le vide : sélection rectangle<br />
        Ctrl+clic : ajouter à la sélection<br />
        Suppr : supprimer la sélection<br />
        R : orientation de la porte sélectionnée<br />
        Maj : forcer l'alignement H/V<br />
        Ctrl+Z / Ctrl+Y : annuler / refaire
      </div>
    </div>
  );
}

/** Propriétés numériques d'un bloc rectangulaire */
function BlockRectProps({ block, onEdit }) {
  const fields = [
    ['x', 'X', block.x, {}], ['y', 'Y', block.y, {}],
    ['w', 'Largeur', block.w, { positive: true }], ['h', 'Hauteur', block.h, { positive: true }],
    ['radius', 'Rayon angles', block.radius ?? 0, { nonNegative: true }],
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
      {fields.map(([key, label, value, constraints]) => (
        <label key={key} style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
          {label}
          <NumField
            value={value} style={{ width: '100%', padding: '3px 5px' }}
            onCommit={(v) => onEdit(key, v)}
            {...constraints}
          />
        </label>
      ))}
    </div>
  );
}

/** Position relative au parent (hiérarchie de l'arborescence) */
function ParentInfo({ floor, item }) {
  if (!item?.parentId) return null;
  const own = itemOrigin(floor, item.id);
  const parentOrigin = itemOrigin(floor, item.parentId);
  if (!own || !parentOrigin) return null;
  const dx = roundCoord(own.x - parentOrigin.x);
  const dy = roundCoord(own.y - parentOrigin.y);
  const fmt = (v) => (v >= 0 ? `+${v}` : `${v}`);
  return (
    <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>
      🔗 Enfant d'un élément — position relative : {fmt(dx)}, {fmt(dy)} cm
      (suit les déplacements du parent)
    </div>
  );
}

function GenerateWallsButton() {
  const project = useProjectStore((s) => s.project);
  const activeFloorId = useProjectStore((s) => s.activeFloorId);
  const mutate = useProjectStore((s) => s.mutate);
  const setStatus = useProjectStore((s) => s.setStatus);
  const chatBusy = useProjectStore((s) => s.chatBusy);
  const floor = project?.floors.find((f) => f.id === activeFloorId);
  const isRealRoom = (r) => r.blocks?.length && (r.kind ?? 'room') === 'room'; // dalles et toits exclus
  const hasShapes = floor?.rooms.some(isRealRoom);

  const generate = () => {
    const specs = generateWallsFromRooms(
      floor.rooms.filter(isRealRoom),
      { extThickness: project.settings.defaultWallThickness, partThickness: 10 },
    );
    if (!specs.length) { setStatus('Aucun contour de pièce à transformer en murs'); return; }
    if (floor.walls.length
      && !window.confirm(`Ajouter ${specs.length} murs générés aux ${floor.walls.length} murs existants ?`)) return;
    // Origine des murs générés : la dalle qui contient leur milieu
    const slabs = floor.rooms.filter((r) => r.kind === 'slab' && r.blocks?.length);
    const parentFor = (s) => slabs.find((sl) => pointInRoom(sl, { x: (s.a.x + s.b.x) / 2, y: (s.a.y + s.b.y) / 2 }))?.id ?? null;
    mutate((floors) => {
      const f = floors.find((x) => x.id === floor.id);
      f.walls.push(...specs.map((s) => ({
        id: newId('w'), a: s.a, b: s.b, thickness: s.thickness, height: null, parentId: parentFor(s),
      })));
    });
    setStatus(`${specs.length} murs générés (Ctrl+Z pour annuler)`);
  };

  return (
    <button onClick={generate} disabled={!hasShapes || chatBusy}
      title="Crée les murs sur les contours des pièces : extérieurs + une cloison par frontière partagée">
      🧱 Générer les murs
    </button>
  );
}

export function PropertiesPanel() {
  const selection = useProjectStore((s) => s.selection);
  const project = useProjectStore((s) => s.project);
  const activeFloorId = useProjectStore((s) => s.activeFloorId);
  const mutate = useProjectStore((s) => s.mutate);
  const setSelection = useProjectStore((s) => s.setSelection);
  if (!selection?.length || !project) return null;
  const floor = project.floors.find((f) => f.id === activeFloorId);
  if (!floor) return null;

  // Sélection multiple : compteur + suppression groupée
  if (selection.length > 1) {
    const doDelete = () => {
      const wallIds = new Set(selection.filter((s) => s.kind === 'wall').map((s) => s.id));
      const openingIds = new Set(selection.filter((s) => s.kind === 'opening').map((s) => s.id));
      const roomIds = new Set(selection.filter((s) => s.kind === 'room').map((s) => s.id));
      mutate((floors) => {
        const f = floors.find((x) => x.id === floor.id);
        f.walls = f.walls.filter((w) => !wallIds.has(w.id));
        f.openings = f.openings.filter((o) => !openingIds.has(o.id) && !wallIds.has(o.wallId));
        f.rooms = f.rooms.filter((r) => !roomIds.has(r.id));
      });
      setSelection([]);
    };
    return (
      <div className="props">
        <h3>{selection.length} éléments sélectionnés</h3>
        <button className="danger" onClick={doDelete}>Supprimer la sélection (Suppr)</button>
      </div>
    );
  }

  const sel = selection[0];

  if (sel.kind === 'wall') {
    const wall = floor.walls.find((w) => w.id === sel.id);
    if (!wall) return null;
    const editWall = (key) => (v) => mutate((floors) => {
      const w = floors.find((f) => f.id === floor.id).walls.find((x) => x.id === wall.id);
      if (w) w[key] = v;
    });
    const editPoint = (end, axis) => (v) => mutate((floors) => {
      const w = floors.find((f) => f.id === floor.id).walls.find((x) => x.id === wall.id);
      if (w) w[end] = { ...w[end], [axis]: v };
    });
    return (
      <div className="props">
        <h3>Mur sélectionné — {fmtLen(wallLength(wall))} cm</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          {[['a', 'x'], ['a', 'y'], ['b', 'x'], ['b', 'y']].map(([end, axis]) => (
            <label key={end + axis} style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
              {`${axis.toUpperCase()} ${end}`}
              <NumField value={wall[end][axis]} style={{ padding: '3px 5px' }} onCommit={editPoint(end, axis)} />
            </label>
          ))}
        </div>
        <label>Épaisseur (cm)
          <NumField value={wall.thickness} positive onCommit={editWall('thickness')} />
        </label>
        <label>Hauteur côté a (cm, vide = étage)
          <NumField value={wall.height} positive allowNull placeholder={String(floor.height)}
            onCommit={editWall('height')} />
        </label>
        <label>Hauteur côté b (cm, vide = comme a) — mur rampant
          <NumField value={wall.heightB} positive allowNull placeholder="= côté a"
            onCommit={editWall('heightB')} />
        </label>
        <h3>Finitions 3D</h3>
        <FinishSelect label="Face extérieure" kind="wall" value={wall.finishOut}
          onCommit={editWall('finishOut')} />
        <FinishSelect label="Face intérieure" kind="wall" value={wall.finishIn}
          onCommit={editWall('finishIn')} />
        <button
          title="Échange les finitions des deux faces (si l'extérieur est du mauvais côté)"
          onClick={() => mutate((floors) => {
            const w = floors.find((f) => f.id === floor.id).walls.find((x) => x.id === wall.id);
            if (w) [w.finishOut, w.finishIn] = [w.finishIn ?? null, w.finishOut ?? null];
          })}
        >
          ⇄ Échanger int / ext
        </button>
        <button
          title="Coupe le mur en deux au milieu — ou double-cliquez sur le mur à l'endroit voulu"
          onClick={() => splitWallAt(useProjectStore, floor, wall, roundCoord(wallLength(wall) / 2))}
        >
          ✂ Couper en deux
        </button>
        <ParentInfo floor={floor} item={wall} />
      </div>
    );
  }

  if (sel.kind === 'opening') {
    const op = floor.openings.find((o) => o.id === sel.id);
    if (!op) return null;
    const isVelux = !op.wallId;
    const edit = (key) => (v) => mutate((floors) => {
      const o = floors.find((f) => f.id === floor.id).openings.find((x) => x.id === op.id);
      if (o) o[key] = v;
    });
    const editPos = (axis) => (v) => mutate((floors) => {
      const o = floors.find((f) => f.id === floor.id).openings.find((x) => x.id === op.id);
      if (o?.position) o.position = { ...o.position, [axis]: v };
    });
    const toggleMeta = (key) => () => mutate((floors) => {
      const o = floors.find((f) => f.id === floor.id).openings.find((x) => x.id === op.id);
      if (o) o.meta = { ...o.meta, [key]: !o.meta?.[key] };
    });
    const title = { door: 'Porte', window: 'Fenêtre', garage: 'Porte de garage', passage: 'Ouverture libre' }[op.type];
    return (
      <div className="props">
        <h3>{title} sélectionnée</h3>
        {!isVelux && op.type !== 'passage' && (
          <label>Variante
            <select value={op.variant ?? 'single'} onChange={(e) => edit('variant')(e.target.value)}>
              {Object.entries(VARIANT_LABELS[op.type]).map(([v, label]) => (
                (op.type !== 'window' || v !== 'velux') && <option key={v} value={v}>{label}</option>
              ))}
            </select>
          </label>
        )}
        {isVelux && <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>Velux (fenêtre de toit)</div>}
        <label>Largeur (cm)<NumField value={op.width} positive onCommit={edit('width')} /></label>
        <label>{isVelux ? 'Profondeur (cm)' : 'Hauteur (cm)'}<NumField value={op.height} positive onCommit={edit('height')} /></label>
        {!isVelux && (
          <label>{op.type === 'passage' ? 'Hauteur basse (cm)' : 'Allège (cm)'}
            <NumField value={op.sillHeight} nonNegative onCommit={edit('sillHeight')} />
          </label>
        )}
        {!isVelux && op.type === 'passage' && (
          <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>
            Hauteur haute = hauteur basse + hauteur ({op.sillHeight + op.height} cm)
          </div>
        )}
        {!isVelux && (
          <label>Linteau arrondi (cm, + convexe / − concave)
            <NumField value={op.arch ?? 0} onCommit={edit('arch')} />
          </label>
        )}
        {!isVelux && <label>Position sur le mur (cm, centre)<NumField value={op.offset} onCommit={edit('offset')} /></label>}
        {!isVelux && (() => {
          // Placement à distance : cotes bord d'ouverture ↔ extrémités du mur porteur
          // (négatif autorisé : l'ouverture déborde alors sur un mur colinéaire joint)
          const wall = floor.walls.find((w) => w.id === op.wallId);
          if (!wall) return null;
          const len = wallLength(wall);
          return (
            <>
              <label>Bord ↔ extrémité a du mur (cm)
                <NumField value={roundCoord(op.offset - op.width / 2)}
                  onCommit={(v) => edit('offset')(v + op.width / 2)} />
              </label>
              <label>Bord ↔ extrémité b du mur (cm)
                <NumField value={roundCoord(len - op.offset - op.width / 2)}
                  onCommit={(v) => edit('offset')(roundCoord(len - v - op.width / 2))} />
              </label>
            </>
          );
        })()}
        {isVelux && (
          <>
            <label>X (cm)<NumField value={op.position?.x ?? 0} onCommit={editPos('x')} /></label>
            <label>Y (cm)<NumField value={op.position?.y ?? 0} onCommit={editPos('y')} /></label>
          </>
        )}
        <ParentInfo floor={floor} item={op} />
        {!isVelux && op.type !== 'passage' && (
          <>
            <button onClick={toggleMeta('flipH')} title="Charnières de l'autre côté (raccourci : R)">
              ⇄ Inverser le battant
            </button>
            <button onClick={toggleMeta('flipV')} title="Ouvre de l'autre côté du mur (raccourci : R)">
              ⇅ Intérieur / extérieur
            </button>
            <h3>Menuiserie (3D)</h3>
            <label>Matériau
              <select value={op.frameMaterial ?? 'pvc'} onChange={(e) => edit('frameMaterial')(e.target.value)}>
                <option value="pvc">PVC</option>
                <option value="wood">Bois</option>
                <option value="alu">Aluminium</option>
              </select>
            </label>
            <label style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={op.open ?? false}
                onChange={(e) => edit('open')(e.target.checked)} />
              {op.type === 'door' || op.type === 'garage' ? 'Porte ouverte' : 'Fenêtre ouverte'}
            </label>
            {op.type !== 'garage' && (
              <>
                <label>Volet
                  <select
                    value={op.shutter === 'swing' ? `swing-${op.shutterStyle ?? 'solid'}` : (op.shutter ?? 'none')}
                    onChange={(e) => {
                      const v = e.target.value;
                      mutate((floors) => {
                        const o = floors.find((x) => x.id === floor.id).openings.find((x) => x.id === op.id);
                        if (!o) return;
                        if (v.startsWith('swing')) {
                          o.shutter = 'swing';
                          o.shutterStyle = v.endsWith('louvered') ? 'louvered' : 'solid';
                        } else {
                          o.shutter = v;
                        }
                      });
                    }}
                  >
                    <option value="none">Aucun</option>
                    <option value="roller">Roulant</option>
                    <option value="swing-solid">Battants pleins</option>
                    <option value="swing-louvered">Battants ajourés (persiennes)</option>
                  </select>
                </label>
                {op.shutter !== 'none' && (
                  <>
                    <label>Couleur du volet (peinture texturée bois)
                      <select value={op.shutterColor ?? 'wood'} onChange={(e) => edit('shutterColor')(e.target.value)}>
                        {Object.entries(SHUTTER_COLORS).map(([v, c]) => (
                          <option key={v} value={v}>{c.label}</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
                      <input type="checkbox" checked={op.shutterClosed ?? false}
                        onChange={(e) => edit('shutterClosed')(e.target.checked)} />
                      Volet fermé
                    </label>
                    <button onClick={toggleMeta('shutterFlip')}
                      title="Si le volet apparaît du mauvais côté du mur (le côté extérieur est détecté via les pièces, mais peut se tromper sans pièce dessinée)">
                      ⇄ Volet de l'autre côté
                    </button>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  }

  if (sel.kind === 'item') {
    const it = (floor.items ?? []).find((x) => x.id === sel.id);
    if (!it) return null;
    const entry = findCatalogItem(useProjectStore.getState().catalog, it.catalogId);
    const edit = (key) => (v) => mutate((floors) => {
      const o = floors.find((f) => f.id === floor.id).items.find((x) => x.id === it.id);
      if (o) o[key] = v;
    });
    return (
      <div className="props">
        <h3>{entry?.name ?? it.catalogId}</h3>
        {entry && (
          <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            {ITEM_CATEGORIES[entry.category]?.label} — {entry.size.w}×{entry.size.d}×{entry.size.h} cm
          </div>
        )}
        <label>Libellé (optionnel)
          <input value={it.label ?? ''} onChange={(e) => edit('label')(e.target.value || null)} />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          <label style={{ fontSize: 10, color: 'var(--text-dim)' }}>X
            <NumField value={it.position.x} style={{ width: '100%' }}
              onCommit={(v) => mutate((floors) => {
                const o = floors.find((f) => f.id === floor.id).items.find((x) => x.id === it.id);
                if (o) o.position = { ...o.position, x: v };
              })} />
          </label>
          <label style={{ fontSize: 10, color: 'var(--text-dim)' }}>Y
            <NumField value={it.position.y} style={{ width: '100%' }}
              onCommit={(v) => mutate((floors) => {
                const o = floors.find((f) => f.id === floor.id).items.find((x) => x.id === it.id);
                if (o) o.position = { ...o.position, y: v };
              })} />
          </label>
        </div>
        <label>Rotation (°, raccourci R)
          <NumField value={it.rotation ?? 0} onCommit={edit('rotation')} />
        </label>
        <label>Hauteur de pose (cm, vide = défaut{entry ? ` : ${entry.elevation === 'ceiling' ? 'plafond' : entry.elevation}` : ''})
          <NumField value={it.elevation} allowNull nonNegative onCommit={edit('elevation')} />
        </label>
        <label>Échelle
          <NumField value={it.scale ?? 1} positive onCommit={edit('scale')} />
        </label>
        <ParentInfo floor={floor} item={it} />
      </div>
    );
  }

  if (sel.kind === 'beam') {
    const beam = (floor.beams ?? []).find((x) => x.id === sel.id);
    if (!beam) return null;
    const edit = (key) => (v) => mutate((floors) => {
      const b = floors.find((f) => f.id === floor.id).beams.find((x) => x.id === beam.id);
      if (b) b[key] = v;
    });
    const editPt = (end, axis) => (v) => mutate((floors) => {
      const b = floors.find((f) => f.id === floor.id).beams.find((x) => x.id === beam.id);
      if (b) b[end] = { ...b[end], [axis]: v };
    });
    const matSelect = (
      <label style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
        Matériau
        <select value={beam.material} onChange={(e) => edit('material')(e.target.value)}>
          {Object.entries(BEAM_MATERIAL_LABELS).map(([v, label]) => (
            <option key={v} value={v}>{label}</option>
          ))}
        </select>
      </label>
    );
    if (beam.kind === 'post') {
      return (
        <div className="props">
          <h3>Poteau — {fmtLen(beam.w)} × {fmtLen(beam.d ?? beam.w)} cm</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            <label style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
              X
              <NumField value={beam.p.x} style={{ padding: '3px 5px' }}
                onCommit={(v) => edit('p')({ ...beam.p, x: v })} />
            </label>
            <label style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
              Y
              <NumField value={beam.p.y} style={{ padding: '3px 5px' }}
                onCommit={(v) => edit('p')({ ...beam.p, y: v })} />
            </label>
            <label style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
              Section X (cm)
              <NumField value={beam.w} positive style={{ padding: '3px 5px' }} onCommit={edit('w')} />
            </label>
            <label style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
              Section Y (vide = carré)
              <NumField value={beam.d} positive allowNull style={{ padding: '3px 5px' }} onCommit={edit('d')} />
            </label>
            <label style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
              Hauteur (vide = étage)
              <NumField value={beam.height} positive allowNull style={{ padding: '3px 5px' }} onCommit={edit('height')} />
            </label>
            <label style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
              Élévation base
              <NumField value={beam.elevation} style={{ padding: '3px 5px' }} onCommit={edit('elevation')} />
            </label>
          </div>
          {matSelect}
          <ParentInfo floor={floor} item={beam} />
        </div>
      );
    }
    const len = Math.hypot(beam.b.x - beam.a.x, beam.b.y - beam.a.y);
    return (
      <div className="props">
        <h3>Poutre — {fmtLen(len)} cm</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          {[['a', 'x'], ['a', 'y'], ['b', 'x'], ['b', 'y']].map(([end, axis]) => (
            <label key={end + axis} style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
              {`${axis.toUpperCase()} ${end}`}
              <NumField value={beam[end][axis]} style={{ padding: '3px 5px' }} onCommit={editPt(end, axis)} />
            </label>
          ))}
          <label style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
            Section largeur (cm)
            <NumField value={beam.w} positive style={{ padding: '3px 5px' }} onCommit={edit('w')} />
          </label>
          <label style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
            Section hauteur (cm)
            <NumField value={beam.h} positive style={{ padding: '3px 5px' }} onCommit={edit('h')} />
          </label>
          <label style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
            Élév. dessous a (vide = plafond)
            <NumField value={beam.elevA} allowNull style={{ padding: '3px 5px' }} onCommit={edit('elevA')} />
          </label>
          <label style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
            Élév. dessous b (vide = comme a)
            <NumField value={beam.elevB} allowNull style={{ padding: '3px 5px' }} onCommit={edit('elevB')} />
          </label>
        </div>
        {matSelect}
        <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>
          Élévations a/b différentes = poutre rampante (chevron, arbalétrier).
        </div>
        <ParentInfo floor={floor} item={beam} />
      </div>
    );
  }

  if (sel.kind === 'dim') {
    const dim = (floor.dims ?? []).find((x) => x.id === sel.id);
    if (!dim) return null;
    const editPt = (end, axis) => (v) => mutate((floors) => {
      const d = floors.find((f) => f.id === floor.id).dims.find((x) => x.id === dim.id);
      if (d) d[end] = { ...d[end], [axis]: v };
    });
    const len = fmtLen(Math.hypot(dim.b.x - dim.a.x, dim.b.y - dim.a.y));
    return (
      <div className="props">
        <h3>Cote — {len} cm</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          {[['a', 'x'], ['a', 'y'], ['b', 'x'], ['b', 'y']].map(([end, axis]) => (
            <label key={end + axis} style={{ fontSize: 10, color: 'var(--text-dim)', display: 'flex', flexDirection: 'column' }}>
              {`${axis.toUpperCase()} ${end}`}
              <NumField value={dim[end][axis]} style={{ padding: '3px 5px' }} onCommit={editPt(end, axis)} />
            </label>
          ))}
        </div>
        <ParentInfo floor={floor} item={dim} />
      </div>
    );
  }

  if (sel.kind === 'stair') {
    const stair = (floor.stairs ?? []).find((s) => s.id === sel.id);
    if (!stair) return null;
    const edit = (key) => (v) => mutate((floors) => {
      const s = floors.find((f) => f.id === floor.id).stairs.find((x) => x.id === stair.id);
      if (s) s[key] = v;
    });
    const kindLabel = { straight: 'Escalier droit / à angles', ladder: 'Échelle de meunier', spiral: 'Colimaçon' }[stair.kind];
    return (
      <div className="props">
        <h3>{kindLabel}</h3>
        {stair.kind !== 'spiral' && (
          <label>Largeur (cm)<NumField value={stair.width} positive onCommit={edit('width')} /></label>
        )}
        {stair.kind === 'spiral' && (
          <>
            <label>Rayon (cm)<NumField value={stair.radius} positive onCommit={edit('radius')} /></label>
            <label>Angle de départ (°)<NumField value={stair.startAngle ?? 0} onCommit={edit('startAngle')} /></label>
            <label>Balayage (°, négatif = sens inverse)<NumField value={stair.sweep ?? 360} onCommit={edit('sweep')} /></label>
          </>
        )}
        <label style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={stair.cutSlab !== false}
            onChange={(e) => edit('cutSlab')(e.target.checked)} />
          Trémie : percer la dalle du niveau supérieur
        </label>
        <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>
          Dessiné du bas vers le haut : moitié haute en pointillés, arrivée en
          pointillés sur l'étage supérieur.
        </div>
        <ParentInfo floor={floor} item={stair} />
      </div>
    );
  }

  if (sel.kind === 'room') {
    const room = floor.rooms.find((r) => r.id === sel.id);
    if (!room) return null;
    const editRoom = (fn) => mutate((floors) => {
      const r = floors.find((f) => f.id === floor.id).rooms.find((x) => x.id === room.id);
      if (r) fn(r);
    });
    const isSlab = room.kind === 'slab';
    const isRoof = room.kind === 'roof';
    return (
      <div className="props">
        <h3>{isSlab ? 'Dalle' : isRoof ? 'Toit' : 'Pièce'}</h3>
        <label>Nom
          <input value={room.name}
            onChange={(e) => editRoom((r) => { if (e.target.value) r.name = e.target.value; })} />
        </label>
        {room.blocks?.length > 0 && (
          <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            Surface : {(roomArea(room) / 10000).toFixed(2)} m²
          </div>
        )}
        {isRoof && (
          <>
            <label>Couverture
              <select value={room.material ?? 'slate'}
                onChange={(e) => editRoom((r) => { r.material = e.target.value; })}>
                {Object.entries(ROOF_MATERIAL_LABELS).map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
            </label>
            <label>Pente (°, 0 = toit plat)
              <NumField value={room.slope ?? 0} nonNegative
                onCommit={(v) => editRoom((r) => { r.slope = v; })} />
            </label>
            <label>Descente vers
              <select value={room.slopeDir ?? 'S'}
                onChange={(e) => editRoom((r) => { r.slopeDir = e.target.value; })}>
                {Object.entries(SLOPE_DIR_LABELS).map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
            </label>
            <label>Hauteur de la base (cm)
              <NumField value={room.elevation ?? 0} nonNegative
                onCommit={(v) => editRoom((r) => { r.elevation = v; })} />
            </label>
            <FinishSelect label="Texture de couverture" kind="roof" value={room.finish}
              autoLabel="Auto (selon le matériau)"
              onCommit={(v) => editRoom((r) => { r.finish = v; })} />
            <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>
              Deux toits mono-pente opposés = toit à deux pans.
            </div>
          </>
        )}
        {!isSlab && !isRoof && (
          <FinishSelect label="Sol (rendu 3D)" kind="floor" value={room.finish}
            autoLabel="Couleur de la pièce"
            onCommit={(v) => editRoom((r) => { r.finish = v; })} />
        )}
        {isSlab && (
          <>
            <label>Épaisseur (cm, vide = réglage projet)
              <NumField value={room.thickness} positive allowNull
                placeholder={String(project.settings.slabThickness)}
                onCommit={(v) => editRoom((r) => { r.thickness = v; })} />
            </label>
            <label>Matériau
              <select value={room.material ?? 'concrete'}
                onChange={(e) => editRoom((r) => { r.material = e.target.value; })}>
                {Object.entries(SLAB_MATERIAL_LABELS).map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
            </label>
            <label>Élévation (cm, &gt; 0 = mezzanine)
              <NumField value={room.elevation ?? 0} nonNegative
                onCommit={(v) => editRoom((r) => { r.elevation = v; })} />
            </label>
          </>
        )}
        {room.blocks?.map((block, i) => (
          <fieldset key={i} style={{ border: '1px solid var(--border)', borderRadius: 4, padding: 6 }}>
            <legend style={{ fontSize: 10, color: 'var(--text-dim)', padding: '0 4px' }}>
              {block.type === 'rect' ? `▭ Bloc ${i + 1}` : `⬠ Bloc ${i + 1} — ${block.points.length} sommets`}
              <button
                className="danger" style={{ padding: '0 5px', marginLeft: 6, fontSize: 10 }}
                title="Supprimer ce bloc"
                onClick={() => editRoom((r) => { r.blocks.splice(i, 1); })}
              >
                ✕
              </button>
            </legend>
            {block.type === 'rect' && (
              <BlockRectProps block={block} onEdit={(key, v) => editRoom((r) => { r.blocks[i][key] = v; })} />
            )}
          </fieldset>
        ))}
        <div style={{ fontSize: 10, color: 'var(--text-dim)', lineHeight: 1.5 }}>
          Poignées : carrés bleus = coins/sommets, ronds verts = courbure des côtés.<br />
          Double-clic : sommet = supprimer, côté = ajouter un sommet, pièce = renommer.
        </div>
        <ParentInfo floor={floor} item={room} />
      </div>
    );
  }
  return null;
}
